import type { CameraState, RenderObject, TableZone, Vec3 } from '@v2/contracts'
import { validateHandZones, type Projection } from '@v2/sdk'
import { quaternionFromBasis } from '@v2/math'

export interface HandState {
  readonly zone: { readonly id: string; readonly label: string; readonly count: number } | null
  readonly hidden: boolean
}

/** A projected hand is held only by its own viewer. Other zones retain table layout. */
export function heldZone(projection: Projection): TableZone | undefined {
  validateHandZones(projection.scene)
  return projection.viewer ? projection.scene.zones.find(zone => zone.role === 'hand' && zone.presentation === 'hand' && zone.owner === projection.viewer) : undefined
}

export interface HandSlot {
  readonly x: number
  readonly y: number
  readonly width: number
  readonly height: number
  readonly angle: number
}

/** Pixel-space fan with bounded silhouettes and an exposed strip for every card. */
export function handSlots(aspects: readonly number[], width: number, height: number): readonly HandSlot[] {
  if (!aspects.length || width <= 0 || height <= 0) return []
  const usable = Math.max(1, Math.min(width - 36, width * 0.78))
  const cardHeight = Math.min(205, height * 0.25, usable / 1.3)
  const count = aspects.length
  const fanAngle = Math.min(0.3, Math.max(0, count - 1) * 0.055)
  const cards = aspects.map((aspect, index) => {
    const angle = count > 1 ? ((index / (count - 1)) - 0.5) * fanAngle : 0
    const ratio = Number.isFinite(aspect) && aspect > 0 ? aspect : 0.714
    const rawWidth = cardHeight * ratio
    const scale = Math.min(1, usable * 0.85 / (rawWidth + cardHeight * Math.abs(Math.sin(angle))))
    const w = rawWidth * scale, h = cardHeight * scale
    return { width: w, height: h, angle, bound: Math.abs(Math.cos(angle)) * w + Math.abs(Math.sin(angle)) * h }
  })
  const offsets = [0]
  for (let i = 1; i < count; i++) offsets.push(offsets[i - 1]! + cards[i - 1]!.bound * 0.76)
  let compress = 1
  for (let i = 1; i < count; i++) compress = Math.min(compress, Math.max(0, usable - cards[i]!.bound) / offsets[i]!)
  const span = Math.max(...cards.map((card, i) => offsets[i]! * compress + card.bound))
  return cards.map((card, index) => ({
    ...card,
    x: (width - span) / 2 + offsets[index]! * compress + card.bound / 2,
    // Leave room below the cards for the hand controls.
    y: height - 48 - card.height / 2 + Math.abs(card.angle) * card.height * 0.32,
  }))
}

const plus = (a: Vec3, b: Vec3, scale: number): Vec3 => [a[0] + b[0] * scale, a[1] + b[1] * scale, a[2] + b[2] * scale]

/** Disposable hand layout; stores IDs and hover weights, never card faces or game state. */
export class HandPresentation {
  private hidden = false
  private hover = new Map<string, number>()
  private zone: HandState['zone'] = null
  private cardIds = new Set<string>()

  update(projection: Projection, baseline = false): HandState {
    const zone = heldZone(projection)
    if (baseline || zone?.id !== this.zone?.id) { this.hidden = false; this.hover.clear() }
    this.cardIds = new Set(zone ? projection.scene.pieces.filter(piece => piece.kind === 'card' && piece.zone === zone.id).map(piece => piece.id) : [])
    this.zone = zone ? { id: zone.id, label: zone.label, count: this.cardIds.size } : null
    for (const id of this.hover.keys()) if (!this.cardIds.has(id)) this.hover.delete(id)
    return this.state
  }
  get state(): HandState { return { zone: this.zone, hidden: this.hidden } }
  show(): void { this.hidden = false }
  toggle(): void { if (this.zone) this.hidden = !this.hidden }
  zoneFor(id: string): string | undefined { return this.cardIds.has(id) ? this.zone?.id : undefined }
  visible(object: RenderObject): boolean { return !this.cardIds.has(object.id) || !this.hidden }
  /** Resting silhouettes remain hoverable as cards lift away from the pointer. */
  pick(x: number, y: number, objects: readonly RenderObject[], width: number, height: number, renderedId: string | null, hoveredId: string | null = null): string | null {
    if (!this.zone || this.hidden || !(width > 0 && height > 0)) return null
    const cards = objects.filter(object => this.cardIds.has(object.id))
    const slots = handSlots(cards.map(card => card.size[0] / card.size[2]), width, height)
    for (let index = cards.length - 1; index >= 0; index--) {
      // Only extend the active hover. Undealt cards must not create invisible
      // targets at their future hand positions before they physically arrive.
      if (cards[index]!.id !== hoveredId) continue
      const slot = slots[index]!
      const dx = x - slot.x, dy = y - slot.y
      const c = Math.cos(slot.angle), s = Math.sin(slot.angle)
      if (Math.abs(dx * c + dy * s) <= slot.width / 2 && Math.abs(-dx * s + dy * c) <= slot.height / 2) return cards[index]!.id
    }
    return renderedId && this.cardIds.has(renderedId) ? renderedId : null
  }
  resolve(objects: readonly RenderObject[], camera: CameraState, width: number, height: number, hoveredId: string | null, seconds: number, immediate = false): readonly RenderObject[] {
    // A hidden or mounting host can report zero dimensions. Wait for a usable
    // viewport instead of producing invalid card slots and stopping the frame loop.
    if (!(width > 0 && height > 0)) return objects.filter(object => !this.cardIds.has(object.id))
    const { azimuth: a, elevation: e, distance, target } = camera
    const right: Vec3 = [Math.cos(a), 0, -Math.sin(a)]
    const up: Vec3 = [-Math.sin(a) * Math.sin(e), Math.cos(e), -Math.cos(a) * Math.sin(e)]
    const back: Vec3 = [Math.sin(a) * Math.cos(e), Math.sin(e), Math.cos(a) * Math.cos(e)]
    const eye = plus(target, back, distance)
    const planeDistance = 3
    const pixelScale = 2 * Math.tan(camera.fov / 2) * planeDistance / Math.max(1, height)
    const result = new Map<string, RenderObject>()
    if (this.zone) {
      const cards = objects.filter(object => this.cardIds.has(object.id))
      const slots = handSlots(cards.map(card => card.size[0] / card.size[2]), width, height)
      cards.forEach((card, index) => {
        const slot = slots[index]!
        const to = card.id === hoveredId ? 1 : 0
        const weight = immediate ? to : (this.hover.get(card.id) ?? 0) + (to - (this.hover.get(card.id) ?? 0)) * (1 - Math.exp(-Math.max(0, seconds) * 18))
        this.hover.set(card.id, weight)
        const angle = slot.angle * (1 - weight)
        const c = Math.cos(angle), s = Math.sin(angle)
        const localRight: Vec3 = plus(right.map(value => value * c) as unknown as Vec3, up, -s)
        const localDepth: Vec3 = plus(up.map(value => -value * c) as unknown as Vec3, right, -s)
        const cardDistance = planeDistance - index * 0.0005 - weight * 0.04
        const perspective = cardDistance / planeDistance
        let position = plus(eye, back, -cardDistance)
        position = plus(position, right, (slot.x - width / 2) * pixelScale * perspective)
        position = plus(position, up, (height / 2 - slot.y + weight * 22) * pixelScale * perspective)
        const scale = pixelScale * perspective * (1 + weight * 0.025)
        result.set(card.id, { ...card, layer: 'foreground', position,
          orientation: quaternionFromBasis(localRight, back, localDepth),
          size: [slot.width * scale, 0.005 * perspective, slot.height * scale], roughness: 0.98 })
      })
    }
    return objects.map(object => result.get(object.id) ?? object)
  }
  clear(): void { this.cardIds.clear(); this.hover.clear(); this.zone = null; this.hidden = false }
}
